/* eslint-disable max-statements */

import { Address, PartialAddress } from '@dreryk/address-common-api';
import {
   MedicalDataAccessAuthorization,
   MedicalDataAccessAuthorizationCreateCommand,
   Patient,
} from '@dreryk/egabinet-patient-frontend-api';
import { AuthorizedPersonExternalIdentifier, PatientId } from '@dreryk/egabinet-patient-published-language-api';
import { FirstName, LastName, MiddleName } from '@dreryk/person-name-common-api';
import { NationalIdentificationNumber, Passport, Pesel } from '@dreryk/personal-external-identifier-common-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { createAuthorizedPersonTypeVisitor } from '../../../common/PersonForm/AuthorizedPersonIdentifier/AuthorizedPersonIdentifierTypeField';
import { patientConsentsAndAuthorizationsTranslationKeys } from '../../../translations';
import { MedicalDataAccessAuthorizationFormBean } from '../MedicalDataAccessAuthorizationModal/MedicalDataAccessAuthorizationModalForm/MedicalDataAccessAuthorizationModalForm.types';
import { useRefetchMedicalDataAccessAuthorization } from '../MedicalDataAccessAuthorizationPanel.hooks';

const useCreateMedicalDataAccessAuthorization = (
   patientId: Patient['id']
): UseCqrsCommandResult<MedicalDataAccessAuthorization, MedicalDataAccessAuthorizationFormBean> => {
   const invalidateMedicalDataAccessAuthorization = useRefetchMedicalDataAccessAuthorization();

   return useCqrsCommand<MedicalDataAccessAuthorization, MedicalDataAccessAuthorizationFormBean>(
      (parameters) => {
         const command = new MedicalDataAccessAuthorizationCreateCommand();
         command.patientId = PatientId.of(patientId);
         command.firstName = FirstName.of(parameters.firstName!);
         if (parameters.middleName) {
            command.middleName = Choice.of(MiddleName.of(parameters.middleName));
         }
         command.lastName = LastName.of(parameters.lastName!);

         createAuthorizedPersonTypeVisitor(parameters.authorizedPersonIdentifierType)({
            visitPesel: () => {
               command.externalIdentifier = Choice.of(
                  AuthorizedPersonExternalIdentifier.of(Pesel.of(parameters.authorizedPersonIdentifierValue!))
               );
            },
            visitPassport: () => {
               command.externalIdentifier = Choice.of(
                  AuthorizedPersonExternalIdentifier.of(
                     Passport.of(
                        parameters.passportCountry!,
                        parameters.authorizedPersonIdentifierValue!,
                        parameters.passportValidTo!
                     )
                  )
               );
            },
            visitNationalIdentificationNumber: () => {
               command.externalIdentifier = Choice.of(
                  AuthorizedPersonExternalIdentifier.of(
                     NationalIdentificationNumber.of(
                        parameters.nationalIdentificationNumberCountry!,
                        parameters.authorizedPersonIdentifierValue!
                     )
                  )
               );
            },
            visitBirthDate: () => {
               command.birthDate = Choice.of(parameters.birthDate);
            },
         });

         command.address = Choice.of<Address>(
            PartialAddress.builder()
               .withCountry(parameters.country)
               .withPostcode(parameters.postcode)
               .withTown(parameters.town)
               .withStreet(parameters.street)
               .withBuilding(parameters.house)
               .withFlat(parameters.flat)
               .build()
         );

         command.phoneNumber = Choice.withUseDefaultIfNull(parameters.phoneNumber);
         command.forDocumentation = parameters.documentationAccess;
         command.forInformation = parameters.informationAccess;
         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(
               patientConsentsAndAuthorizationsTranslationKeys.authorizations.authorizationAdded
            );
            invalidateMedicalDataAccessAuthorization();
         },
      }
   );
};

export { useCreateMedicalDataAccessAuthorization };
